import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from "@tanstack/react-query";
import {Chat, ChatDocument, ChatHistoryTuple, ChatSubmitPayload} from "../../util/types";
import {ChatDocumentUploadResponseDto, ChatDto, ChatHeaderDto, ChatIndexResult, ChatResponseDto} from "../types";
import {MessageRole} from "../../util/role";
import {ApiError, ensureOk, request, requestJson} from "../apiUtils";

export const chatHistoryListKey = ["chat-history-list"] as const;
export const chatHistoryDetailKey = (chatId: string) => ["chat-history", chatId] as const;
export const chatIndexMutationKey = ["chat-index"] as const;
export const chatEditMutationKey = ["chat-edit"] as const;
export const chatDocumentUploadMutationKey = ["chat-document-upload"] as const;
export const chatDocumentDeleteMutationKey = ["chat-document-delete"] as const;

const requestChatHistory = (chatId: string) => requestJson<ChatDto>(`/chats/${chatId}`, {method: "GET"});

const toChat = (queryResponse: ChatDto): Chat => ({
    chatId: queryResponse.chatId,
    messages: queryResponse.messages.map((msgDTO) => ({
        content: msgDTO.content,
        chatIndex: msgDTO.chatIndex,
        role: msgDTO.role as MessageRole,
        timestamp: msgDTO.timestamp,
    })),
    documents: queryResponse.documents.map((documentDto) => ({
        documentId: documentDto.documentId,
        filename: documentDto.filename,
        status: documentDto.status,
    })),
});

const fetchChatHistory = (queryClient: QueryClient, chatId: string) =>
    queryClient.fetchQuery({
        queryKey: chatHistoryDetailKey(chatId),
        queryFn: () => requestChatHistory(chatId),
    });

export function useChatHistoryList(): UseQueryResult<ChatHistoryTuple[]> {
    return useQuery<ChatHeaderDto[], Error, ChatHistoryTuple[]>({
        queryKey: chatHistoryListKey,
        queryFn: () => requestJson<ChatHeaderDto[]>("/chats", {method: "GET"}),
        select: (queryResponse) =>
            queryResponse.map((chatHeaderDTO) => ({
                chatId: chatHeaderDTO.chatId,
                title: chatHeaderDTO.title,
            })),
    });
}

export function useChatHistory(chatId: string | undefined): UseQueryResult<Chat> {
    return useQuery<ChatDto, Error, Chat>({
        queryKey: chatHistoryDetailKey(chatId ?? "new"),
        queryFn: () => requestChatHistory(chatId as string),
        enabled: Boolean(chatId),
        select: toChat,
    });
}

type RenameChatPayload = {
    chatId: string;
    newName: string;
};

export function useRenameChat(): UseMutationResult<string, Error, RenameChatPayload> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({chatId, newName}) => {
            const queryResponse = await requestJson<{new_title: string}>(`/chats/${chatId}`, {
                method: "PATCH",
                body: JSON.stringify({new_title: newName}),
            });

            return queryResponse.new_title;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: chatHistoryListKey});
        },
    });
}

type DeleteChatPayload = {
    chatId: string;
};

export function useDeleteChat(): UseMutationResult<void, Error, DeleteChatPayload> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({chatId}) => {
            const response = await request(`/chats/${chatId}`, {
                method: "DELETE",
            });
            await ensureOk(response);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: chatHistoryListKey});
        },
        onError: async(error) => {
            if (error instanceof ApiError && error.code === "NOT_FOUND") {
                await queryClient.invalidateQueries({queryKey: chatHistoryListKey});
            }
        },
    });
}

export function useChatIndex(): UseMutationResult<ChatIndexResult, Error, ChatSubmitPayload> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: chatIndexMutationKey,
        mutationFn: async({chat}) => {
            const requestChat = {
                chatId: chat.chatId,
                messages: chat.messages.map(({content, role, timestamp}) => ({
                    content,
                    role,
                    timestamp,
                })),
            };
            const response = await request("/chats", {
                method: "POST",
                body: JSON.stringify({text: requestChat}),
            });
            const okResponse = await ensureOk(response);
            const queryResponse = (await okResponse.json()) as ChatResponseDto;

            return {
                chatId: queryResponse.chatId,
                message: {
                    content: queryResponse.content,
                    chatIndex: queryResponse.chatIndex,
                    role: queryResponse.role,
                    timestamp: queryResponse.timestamp,
                },
            };
        },
        onError: async(error, {chat}) => {
            if (!chat.chatId || !(error instanceof ApiError)) {
                return;
            }

            if (error.code === "CHAT_DOCUMENTS_UNAVAILABLE") {
                await fetchChatHistory(queryClient, chat.chatId);
                return;
            }

            if (error.status === 409) {
                await Promise.all([
                    fetchChatHistory(queryClient, chat.chatId),
                    queryClient.invalidateQueries({queryKey: chatHistoryListKey}),
                ]);
            }
        },
    });
}

type EditChatMessagePayload = {
    chatId: string;
    messageIndex: number;
    message: Chat["messages"][number];
};

export function useEditChatMessage(): UseMutationResult<Chat, Error, EditChatMessagePayload> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: chatEditMutationKey,
        mutationFn: async({chatId, messageIndex, message}) => {
            const queryResponse = await requestJson<ChatDto>(`/chats/${chatId}/${messageIndex}`, {
                method: "PATCH",
                body: JSON.stringify({
                    content: message.content,
                    role: message.role,
                    timestamp: String(message.timestamp),
                }),
            });

            return toChat(queryResponse);
        },
        onSuccess: async(editedChat, {chatId}) => {
            if (editedChat.chatId) {
                queryClient.setQueryData(chatHistoryDetailKey(editedChat.chatId), editedChat);
            }
            queryClient.removeQueries({queryKey: chatHistoryDetailKey(chatId), exact: true});
            await queryClient.invalidateQueries({queryKey: chatHistoryListKey});
        },
    });
}

type UploadChatDocumentPayload = {
    chatId: string | undefined;
    file: File;
};

type UploadChatDocumentResult = {
    chatId: string;
    document: ChatDocument;
};

export function useUploadChatDocument(): UseMutationResult<UploadChatDocumentResult, Error, UploadChatDocumentPayload> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: chatDocumentUploadMutationKey,
        mutationFn: async({chatId, file}) => {
            const formData = new FormData();
            if (chatId) {
                formData.append("chatId", chatId);
            }
            formData.append("file", file);

            const response = await request("/chat-documents", {
                method: "POST",
                body: formData,
            });
            const okResponse = await ensureOk(response);
            const queryResponse = (await okResponse.json()) as ChatDocumentUploadResponseDto;

            return {
                chatId: queryResponse.chatId,
                document: {
                    documentId: queryResponse.document.documentId,
                    filename: queryResponse.document.filename,
                    status: queryResponse.document.status,
                },
            };
        },
        onSuccess: async({chatId}) => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: chatHistoryListKey}),
                queryClient.invalidateQueries({queryKey: chatHistoryDetailKey(chatId)}),
            ]);
        },
    });
}

type DeleteChatDocumentPayload = {
    chatId: string;
    documentId: string;
};

export function useDeleteChatDocument(): UseMutationResult<void, Error, DeleteChatDocumentPayload> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: chatDocumentDeleteMutationKey,
        onMutate: async({chatId, documentId}) => {
            await queryClient.cancelQueries({queryKey: chatHistoryDetailKey(chatId)});
            const previousChat = queryClient.getQueryData<Chat>(chatHistoryDetailKey(chatId));

            //optimistically delete document from UI
            if (previousChat) {
                queryClient.setQueryData<Chat>(chatHistoryDetailKey(chatId), {
                    ...previousChat,
                    documents: previousChat.documents.filter((document) => document.documentId !== documentId),
                });
            }

            return {previousChat};
        },
        mutationFn: async({chatId, documentId}) => {
            const response = await request(`/chats/${chatId}/documents/${documentId}`, {
                method: "DELETE",
            });
            await ensureOk(response);
        },
        onSuccess: async(_, {chatId}) => {
            await Promise.all([
                queryClient.invalidateQueries({queryKey: chatHistoryListKey}),
                queryClient.invalidateQueries({queryKey: chatHistoryDetailKey(chatId)}),
            ]);
        },
        onError: async(error, {chatId}, context) => {
            if (error instanceof ApiError && error.code === "NOT_FOUND") {
                await Promise.all([
                    queryClient.invalidateQueries({queryKey: chatHistoryListKey}),
                    queryClient.invalidateQueries({queryKey: chatHistoryDetailKey(chatId)}),
                ]);
                return;
            }

            if (context?.previousChat) {
                queryClient.setQueryData(chatHistoryDetailKey(chatId), context.previousChat);
            }
        },
    });
}
